import { axios } from '@ori/axios';
import { ACCEPT_LANGUAGE } from '@ori/fetching';
import type { Dispatch, FC, PropsWithChildren, SetStateAction } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import type { SuggestionsAndResults } from '../../models';
import { useAppConfig } from '../configurationContext';

export interface SearchInputContext {
  blur: () => void;
  clearResults: () => void;
  focus: () => void;
  hide: () => void;
  isFocused: boolean;
  isVisible: boolean;
  results?: SuggestionsAndResults | undefined;
  search: (query: string) => void;
  setValue: Dispatch<SetStateAction<string>>;
  show: () => void;
  timestamp: number;
  value: string;
  manualShow: () => void;
  manualHide: () => void;
  isManuallyOpened: boolean;
}

export const searchInputContext = createContext<SearchInputContext | undefined>(undefined);

const { Provider } = searchInputContext;

export const SearchInputProvider: FC<PropsWithChildren> = ({ children }) => {
  const { showSearchOnScroll } = useAppConfig();
  const [isFocused, setIsFocused] = useState(false);
  const [isVisible, setIsVisible] = useState(Boolean(showSearchOnScroll));
  const [isManuallyOpened, setIsManuallyOpened] = useState(false);
  const [results, setResults] = useState<SuggestionsAndResults | undefined>(undefined);
  const [timestamp, setTimestamp] = useState(0);
  const [value, setValue] = useState('');
  const blur = useCallback(() => setIsFocused(false), []);
  const focus = useCallback(() => setIsFocused(true), []);
  const hide = useCallback(() => setIsVisible(false), []);
  const show = useCallback(() => setIsVisible(true), []);
  const manualShow = useCallback(() => setIsManuallyOpened(true), []);
  const manualHide = useCallback(() => setIsManuallyOpened(false), []);

  const { currentLanguage } = useAppConfig();
  const clearResults = useCallback(() => setResults(undefined), []);
  const search = useCallback(
    (query: string) => {
      const url = new URL(`${window.location.origin}/system/ajax/Search/SuggestionsAndResults`);
      url.searchParams.set('q', query);
      axios
        .get<SuggestionsAndResults>(url.toString(), {
          headers: {
            [ACCEPT_LANGUAGE]: currentLanguage,
          },
        })
        .then(({ data }) => {
          setResults(data);
          setTimestamp(Date.now());
        })
        // eslint-disable-next-line no-console -- log error to console
        .catch(console.error);
    },
    [currentLanguage],
  );
  const context = useMemo(
    () => ({
      blur,
      clearResults,
      focus,
      hide,
      isFocused,
      isManuallyOpened,
      isVisible,
      manualHide,
      manualShow,
      results,
      search,
      setValue,
      show,
      timestamp,
      value,
    }),
    [
      blur,
      clearResults,
      focus,
      hide,
      isFocused,
      isManuallyOpened,
      isVisible,
      manualHide,
      manualShow,
      results,
      search,
      show,
      timestamp,
      value,
    ],
  );

  return <Provider value={context}>{children}</Provider>;
};

export const useSearchInput = () => {
  const context = useContext(searchInputContext);
  if (context) {
    return context;
  }
  throw new Error('useSearchInput must be used within a SearchInputProvider.');
};
